import { useState, useEffect, useCallback, useRef } from 'react';

interface PaginationParams {
  page: number;
  limit: number;
}

interface PaginationResult<T> {
  data: T[];
  total: number;
  totalPages: number;
}

interface UseServerPaginationOptions<T> {
  defaultLimit?: number;
  fetchData: (params: PaginationParams) => Promise<PaginationResult<T>>;
  deps?: any[];
}

interface UseServerPaginationReturn<T> {
  data: T[];
  isLoading: boolean;
  error: Error | null;
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  refresh: () => void;
  reset: () => void;
}

export function useServerPagination<T>({
  defaultLimit = 20,
  fetchData,
  deps = [],
}: UseServerPaginationOptions<T>): UseServerPaginationReturn<T> {
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(defaultLimit);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchDataInternal = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await fetchData({ page, limit });
      if (isMountedRef.current) {
        setData(result.data);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      }
    } catch (err) {
      if (isMountedRef.current) {
        setError(err instanceof Error ? err : new Error('Unknown error'));
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  }, [page, limit, fetchData]);

  useEffect(() => {
    fetchDataInternal();
  }, [fetchDataInternal, ...deps]);

  const handleSetPage = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleSetLimit = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setPage(1); // Reset to page 1 when changing limit
  }, []);

  const refresh = useCallback(() => {
    fetchDataInternal();
  }, [fetchDataInternal]);

  const reset = useCallback(() => {
    setPage(1);
    setLimit(defaultLimit);
  }, [defaultLimit]);

  return {
    data,
    isLoading,
    error,
    page,
    limit,
    total,
    totalPages,
    setPage: handleSetPage,
    setLimit: handleSetLimit,
    refresh,
    reset,
  };
}